<template>
  <section id="contact" class="contact">
    <div class="container">
      <div class="section-heading">
        <div class="heading-line"/>
        <h2 class="section-title">Contact</h2>
      </div>
      <p class="section-subtitle">
        Have a project in mind or just want to say hi? Reach out through any channel below, or send a message directly.
      </p>

      <div class="contact-grid">
        <div class="contact-channels">
          <a :href="`mailto:${personalInfo.email}`" class="contact-channel">
            <div class="contact-channel__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div class="contact-channel__info">
              <span class="contact-channel__label">Email</span>
              <span class="contact-channel__value">{{ personalInfo.email }}</span>
            </div>
          </a>

          <a v-if="xUrl" :href="xUrl" target="_blank" rel="noopener noreferrer" class="contact-channel">
            <div class="contact-channel__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
              </svg>
            </div>
            <div class="contact-channel__info">
              <span class="contact-channel__label">X</span>
              <span class="contact-channel__value">{{ xHandle }}</span>
            </div>
          </a>

          <a v-if="redditUrl" :href="redditUrl" target="_blank" rel="noopener noreferrer" class="contact-channel">
            <div class="contact-channel__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
              </svg>
            </div>
            <div class="contact-channel__info">
              <span class="contact-channel__label">Reddit</span>
              <span class="contact-channel__value">{{ redditHandle }}</span>
            </div>
          </a>

          <!-- Switched off in the admin, this stays in the list and says so,
               with a way to ask. Removing the row would leave a visitor
               wondering whether there is a résumé at all. -->
          <component
            :is="resumeAvailable ? 'a' : 'button'"
            :href="resumeAvailable ? resumeUrl : undefined"
            :target="resumeAvailable ? '_blank' : undefined"
            :rel="resumeAvailable ? 'noopener noreferrer' : undefined"
            :type="resumeAvailable ? undefined : 'button'"
            class="contact-channel"
            @click="resumeAvailable ? undefined : (askingForResume = true)"
          >
            <div class="contact-channel__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="8" y1="13" x2="16" y2="13"/>
                <line x1="8" y1="17" x2="13" y2="17"/>
              </svg>
            </div>
            <div class="contact-channel__info">
              <span class="contact-channel__label">Résumé</span>
              <span class="contact-channel__value">
                {{ resumeAvailable ? 'Opens as PDF' : 'Not available, ask for it' }}
              </span>
            </div>
          </component>

          <ResumeRequestModal :open="askingForResume" @close="askingForResume = false" />

          <a v-if="githubUrl" :href="githubUrl" target="_blank" rel="noopener noreferrer" class="contact-channel">
            <div class="contact-channel__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
              </svg>
            </div>
            <div class="contact-channel__info">
              <span class="contact-channel__label">GitHub</span>
              <span class="contact-channel__value">GitHub Profile</span>
            </div>
          </a>
        </div>

        <!-- Registration does NOT come from this markup. Netlify parses HTML in
             the publish directory at deploy time, and relying on the app's
             prerendered output for that failed: submissions returned 404, which
             is what happens when no form is registered and the POST falls
             through to the static file server. The form is declared for Netlify
             in public/__forms.html instead — keep the two field lists in step,
             because Netlify drops fields it has not seen declared.

             The attributes are kept here anyway so this stays a valid form on
             its own. The POST is done by hand in sendMessage(), since
             @submit.prevent stops the native one. -->
        <form
          ref="formEl"
          class="contact-form"
          name="contact"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          @submit.prevent="sendMessage"
        >
          <!-- Tells Netlify which form a submission belongs to. -->
          <input type="hidden" name="form-name" value="contact">

          <!-- Spam trap: hidden from people, but a bot filling every field
               gives itself away. Netlify discards anything with this set. -->
          <p class="contact-form__trap" aria-hidden="true">
            <label>
              Leave this field empty
              <input v-model="form.botField" name="bot-field" tabindex="-1" autocomplete="off">
            </label>
          </p>

          <div class="contact-form__row">
            <div class="form-field">
              <label for="name">Name</label>
              <input id="name" v-model="form.name" name="name" type="text" required maxlength="80" :class="{ 'has-error': errors.name }">
              <span v-if="errors.name" class="form-field__error">{{ errors.name }}</span>
            </div>

            <div class="form-field">
              <label for="email">Email</label>
              <input id="email" v-model="form.email" name="email" type="email" required maxlength="120" :class="{ 'has-error': errors.email }">
              <span v-if="errors.email" class="form-field__error">{{ errors.email }}</span>
            </div>
          </div>

          <div class="form-field">
            <label for="subject">Subject</label>
            <input id="subject" v-model="form.subject" name="subject" type="text" required maxlength="120" :class="{ 'has-error': errors.subject }">
            <span v-if="errors.subject" class="form-field__error">{{ errors.subject }}</span>
          </div>

          <div class="form-field">
            <label for="message">Message</label>
            <textarea id="message" v-model="form.message" name="message" rows="5" required maxlength="1000" :class="{ 'has-error': errors.message }"/>
            <span v-if="errors.message" class="form-field__error">{{ errors.message }}</span>
          </div>

          <button type="submit" class="contact-submit" :disabled="isSubmitting">
            {{ isSubmitting ? 'Sending…' : 'Send Message' }}
          </button>

        </form>
      </div>
    </div>

    <!-- Teleported to <body> on purpose. `position: fixed` resolves against the
         nearest transformed ancestor rather than the viewport, and the
         horizontal track is permanently translated on X — left in place this
         would anchor to the track's origin and appear over the first panel. -->
    <Teleport to="body">
      <Transition name="submit-fade">
        <div v-if="status" class="submit-overlay" @click="dismiss">
          <div
            class="submit-card"
            :class="`is-${status}`"
            :role="status === 'error' ? 'alert' : 'status'"
            aria-live="polite"
            @click.stop
          >
            <div class="submit-card__mark">
              <span v-if="status === 'sending'" class="submit-spinner"/>

              <svg v-else viewBox="0 0 52 52" class="submit-glyph">
                <circle class="submit-glyph__ring" cx="26" cy="26" r="23" fill="none" stroke-width="2" />
                <path
                  v-if="status === 'success'"
                  class="submit-glyph__stroke"
                  fill="none"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 27l7.5 7.5L37 20"
                />
                <g v-else class="submit-glyph__stroke" fill="none" stroke-width="3" stroke-linecap="round">
                  <path d="M19 19l14 14" />
                  <path d="M33 19l-14 14" />
                </g>
              </svg>
            </div>

            <p class="submit-card__text selectable">{{ statusMessage }}</p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { personalInfo } from '~/data/content';

// The résumé can be switched off from the admin, in which case this row asks
// for it instead of opening it.
const { url: resumeUrl, available: resumeAvailable } = useResume();
const askingForResume = ref(false);

// Form state
const form = reactive({
  name: '',
  email: '',
  subject: '',
  message: '',
  // Honeypot. Stays empty for real people; anything here means a bot.
  botField: ''
});

const errors = reactive({});
const formEl = ref(null);
const isSubmitting = ref(false);

// null | 'sending' | 'success' | 'error' — drives the overlay.
const status = ref(null);
const statusMessage = computed(() => {
  if (status.value === 'sending') return 'Sending your message…';
  if (status.value === 'success') return "Message sent. I'll get back to you within 24 hours.";
  if (status.value === 'error') return `That didn't send. You can email me directly at ${personalInfo.email}.`;
  return '';
});

// The overlay closes itself, but a click dismisses it early — so the timer has
// to be cancellable, or a stale one would close the *next* submission's
// overlay partway through.
let dismissTimer = null;

function closeAfter(ms) {
  clearTimeout(dismissTimer);
  dismissTimer = setTimeout(() => { status.value = null; }, ms);
}

function dismiss() {
  // Never let a click cancel an in-flight request's own feedback.
  if (status.value === 'sending') return;
  clearTimeout(dismissTimer);
  status.value = null;
}
const githubUrl = computed(() => personalInfo.socialLinks.find((link) => link.name === 'GitHub')?.url || '');
const xUrl = computed(() => personalInfo.socialLinks.find((link) => link.name === 'X')?.url || '');
const redditUrl = computed(() => personalInfo.socialLinks.find((link) => link.name === 'Reddit')?.url || '');
const xHandle = computed(() => {
  const username = xUrl.value.match(/x\.com\/([^/?#]+)/i)?.[1];
  return username ? `@${username}` : 'X Profile';
});
const redditHandle = computed(() => {
  const username = redditUrl.value.match(/reddit\.com\/user\/([^/?#]+)/i)?.[1];
  return username ? `u/${username}` : 'Reddit';
});

// Form validation
const validateForm = () => {
  const newErrors = {};

  if (!form.name.trim()) {
    newErrors.name = 'Name is required';
  }

  if (!form.email.trim()) {
    newErrors.email = 'Email is required';
  } else if (!/\S+@\S+\.\S+/.test(form.email)) {
    newErrors.email = 'Please enter a valid email';
  }

  if (!form.subject.trim()) {
    newErrors.subject = 'Subject is required';
  }

  if (!form.message.trim()) {
    newErrors.message = 'Message is required';
  } else if (form.message.length > 1000) {
    newErrors.message = 'Message is too long';
  }

  Object.keys(errors).forEach(key => { errors[key] = ''; });
  Object.assign(errors, newErrors);

  return Object.keys(newErrors).length === 0;
};

// Netlify expects a URL-encoded body, not JSON or FormData.
const encode = (data) =>
  Object.entries(data)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

// Form submission.
//
// Posted to the site root, which is where Netlify listens; `form-name` is what
// routes it to the right form. This only works on a deployed Netlify site —
// locally there is nothing to receive the POST, so it fails and says so.
const sendMessage = async () => {
  if (!validateForm()) return;

  isSubmitting.value = true;
  clearTimeout(dismissTimer);
  status.value = 'sending';

  try {
    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': 'contact',
        'bot-field': form.botField,
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      }),
    });

    // Netlify answers 200 on success. Anything else means it did not land, and
    // claiming otherwise would be worse than saying nothing — the previous
    // version of this function showed the thank-you regardless.
    if (!response.ok) throw new Error(`Netlify responded ${response.status}`);

    Object.keys(form).forEach((key) => { form[key] = ''; });

    status.value = 'success';
    closeAfter(2600);
  } catch (error) {
    console.error('Message failed to send:', error);
    status.value = 'error';
    // Longer than the success hold: this one carries an email address to read,
    // and the form behind it still has their text in it.
    closeAfter(6000);
  } finally {
    isSubmitting.value = false;
  }
};

onUnmounted(() => {
  clearTimeout(dismissTimer);
});

// Lifecycle
onMounted(() => {
  // Native validation is switched off here rather than with a `novalidate`
  // attribute in the markup. The distinction matters: with the script running,
  // our own inline messages are better than the browser's bubbles — but if the
  // script never runs, `required` is the only thing standing between a stray
  // click and an empty submission, and it stays in force.
  if (formEl.value) formEl.value.noValidate = true;
});
</script>

<style lang="scss" scoped>
.contact {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 0;
  background: transparent;
  position: relative;
  overflow: hidden;

  .container {
    width: 100%;
  }

  // Unpinned below this, so the padding is the only gap between sections.
  @media (max-width: 768px) {
    padding: clamp(3rem, 9vh, 4.5rem) 0;
  }
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  text-align: left;
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0 0 clamp(1.25rem, 3vh, 2.5rem);
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: clamp(1.5rem, 4vw, 3rem);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.contact-channels {
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1.2vh, 0.75rem);
}

.contact-channel {
  display: flex;
  align-items: center;
  gap: 1rem;
  // The résumé row is a <button> when the file is switched off, and a button
  // brings its own font, background and centred text.
  width: 100%;
  padding: clamp(0.6rem, 1vh, 0.85rem) 1rem;
  border: 1px solid rgba(var(--border-rgb), 0.3);
  border-radius: 12px;
  background: transparent;
  font: inherit;
  text-align: left;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover {
    border-color: rgba(var(--accent-rgb), 0.4);
    background: rgba(var(--accent-rgb), 0.05);
    text-decoration: none;
  }
}

.contact-channel__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background:
    // Layered over the page colour rather than left translucent. At 10% alpha
    // the HoneycombField backdrop reads straight through the tile, which looks
    // like the comb is painted on top of it. Renders identically, but opaque.
    linear-gradient(rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.1)),
    var(--background);
  color: var(--accent);
  flex-shrink: 0;
}

.contact-channel__info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.contact-channel__label {
  font-size: 0.72rem;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

// An address to write to and a handle to search for are exactly the text
// somebody is meant to lift off this page, so selection stays on here against
// the site-wide default in app.vue.
.contact-channel__value {
  -webkit-user-select: text;
  user-select: text;
  font-size: 0.9rem;
  color: var(--text);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 1.5vh, 1rem);
}

.contact-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(0.75rem, 1.5vh, 1rem);

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  label {
    font-size: 0.78rem;
    color: var(--muted);
    font-weight: 600;
  }

  input,
  textarea {
    padding: 0.6rem 0.85rem;
    border-radius: 10px;
    border: 1px solid rgba(var(--border-rgb), 0.4);
    // Layered over the page colour rather than left at 5% alpha. Translucent,
    // the HoneycombField backdrop showed straight through the fields and made
    // the text hard to read as the light passed over them. Same rendered
    // shade, but opaque.
    background:
      linear-gradient(rgba(var(--muted-rgb), 0.05), rgba(var(--muted-rgb), 0.05)),
      var(--background);
    color: var(--text);
    font-family: inherit;
    // 16px, not 0.9rem. Anything under 16 makes iOS zoom the whole page the
    // moment the field takes focus, and it does not zoom back out. Every other
    // form on the site was already at 16; this one was the exception.
    font-size: 16px;
    transition: border-color 0.2s ease-in-out;

    &:focus {
      outline: none;
      border-color: var(--accent);
    }

    &.has-error {
      border-color: #ef4444;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
}

.form-field__error {
  font-size: 0.72rem;
  color: #ef4444;
}

.contact-submit {
  align-self: flex-start;
  padding: 0.7rem 1.75rem;
  border-radius: 999px;
  border: none;
  background: var(--accent);
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  // Full width on a phone. Hugging its own text on the left of a column of
  // full-width fields reads as an afterthought rather than as the thing the
  // form is for.
  @media (max-width: 768px) {
    align-self: stretch;
  }
}

// Submission overlay. Not scoped to the section — it is teleported to <body>,
// so a scoped selector would still match (Vue keeps the data attribute on the
// teleported nodes), but it is worth remembering it renders outside this tree.
.submit-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.45);
}

.submit-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 340px;
  padding: 2rem 1.75rem;
  border-radius: 18px;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  text-align: center;
}

.submit-card__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
}

.submit-card__text {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--text);
}

.submit-spinner {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid rgba(var(--accent-rgb), 0.18);
  border-top-color: var(--accent);
  animation: submit-spin 0.8s linear infinite;
}

@keyframes submit-spin {
  to { transform: rotate(360deg); }
}

// The ring draws first, then the mark inside it, so the result reads as being
// stamped rather than simply appearing.
.submit-glyph {
  width: 52px;
  height: 52px;

  &__ring {
    stroke-dasharray: 145;
    stroke-dashoffset: 145;
    animation: submit-draw 0.45s ease-out forwards;
  }

  &__stroke {
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: submit-draw 0.35s ease-out 0.3s forwards;
  }
}

@keyframes submit-draw {
  to { stroke-dashoffset: 0; }
}

.submit-card.is-success .submit-glyph {
  .submit-glyph__ring { stroke: rgba(var(--accent-rgb), 0.35); }
  .submit-glyph__stroke { stroke: var(--accent); }
}

.submit-card.is-error .submit-glyph {
  .submit-glyph__ring { stroke: rgba(220, 38, 38, 0.3); }
  .submit-glyph__stroke { stroke: #dc2626; }
}

.submit-fade-enter-active,
.submit-fade-leave-active {
  transition: opacity 0.25s ease;

  .submit-card {
    transition: transform 0.25s ease;
  }
}

.submit-fade-enter-from,
.submit-fade-leave-to {
  opacity: 0;

  .submit-card {
    transform: translateY(8px) scale(0.97);
  }
}

@media (prefers-reduced-motion: reduce) {
  .submit-spinner {
    animation-duration: 1.6s;
  }

  .submit-glyph__ring,
  .submit-glyph__stroke {
    animation: none;
    stroke-dashoffset: 0;
  }
}

// Honeypot. Taken out of the layout and out of the tab order, but deliberately
// NOT display:none — some bots skip hidden fields, and one that fills this in
// is exactly what we want to catch.
.contact-form__trap {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: 0;
}

// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
